// Ask AI on the Pre-route page: "how many pre-routes alert?", "what's waiting for Ross?". The page
// registers itself with the template's chat widget as Shipping does (`axai-shipping.ts`); its one
// tool reads `/api/routing/ask/pre-routes` (read only) within the page's view — the rows on screen
// — unless the question asks past it. The order value only for a manager: the server decides.

import { useEffect, useRef } from "react";
import type { PreRouteTab, PreRouteView, SourceFilter } from "@app/shared";
import { ALERT_BUSINESS_DAYS, APP_PAGES } from "@app/shared";
import { apiPost } from "@axny/platform-ui";
import type { AxaiApp, Tool } from "./axai-prompt";
import { askPath } from "./axai-prompt";

export interface PreRouteAskPage {
  today: string;
  tab: PreRouteTab;
  /** The pre-routes the page reads: the person's customers' only while My customers is on. */
  views: readonly PreRouteView[];
  /** My customers is on: even a question past the view stays within their customers. */
  mine: boolean;
  /** The pre-routes the page's search and customer filter keep, every tab. */
  viewIds: string[];
  /** Rows on screen: those of the tab. */
  onScreen: number;
  search: string;
  customers: string[];
  /** The Source filter: A2000, Deposco, or both. */
  source: SourceFilter;
  manager: boolean;
}

export const PRE_ROUTE_TOOLS: Tool[] = [
  {
    name: "query_pre_routes",
    description:
      "Count and list pre-routes: A2000 orders routed before their pick ticket exists (and Deposco SOs pre-routed " +
      "before 11/02), with their pickup appointment, routing ref, where each stands (no pick " +
      "ticket yet / not on the Worklist yet, pick ticket not in WMS yet / waiting for a group, waiting to move, moved, " +
      "cancelled or shipped) and its alert. Counts by tab (waiting, alerts, moved, all) and alerts (soon, late). " +
      "Within the page's view (its tab, filters and Source) by default.",
    input_schema: {
      type: "object",
      properties: {
        tab: { type: "string", enum: ["waiting", "alerts", "moved", "all"] },
        source: { type: "string", enum: ["A2000", "Deposco"], description: "One source only." },
        customer: { type: "array", items: { type: "string" }, description: "Customer names or parts of them." },
        search: { type: "string", description: "An order # (CTRL_NO), SO #, PO, customer or routing ref." },
        ignore_view: {
          type: "boolean",
          description: "true only when the question asks past the page's tab and filters ('all pre-routes').",
        },
        limit: { type: "integer", description: "Rows to list, 0–25 (default 10). Counts cover every match." },
      },
    },
  },
];

function systemPrompt(p: PreRouteAskPage) {
  return [
    {
      type: "text" as const,
      text: [
        "You are Ask AI in Production & Routing, the AXNY warehouse's app. The user is on the Pre-route page, " +
          `${p.tab} tab${p.mine ? ", my customers" : ""}${p.source !== "all" ? `, ${p.source} only` : ""}${p.search ? `, searching "${p.search}"` : ""}${p.customers.length ? `, customers ${p.customers.join(", ")}` : ""}. ` +
          `On screen: ${p.onScreen} of ${p.views.length} pre-routes.`,
        APP_PAGES,
        `Today is ${p.today} (America/New_York).`,
        "## Definitions",
        "- A pre-route is an A2000 order (the order # is CTRL_NO, 7 digits; the PO is ORDER_NO) routed before its " +
          "pick ticket exists. It moves to its Worklist lines by itself once a pick ticket of the order is in the " +
          "WMS: Routing Reference, Routing status Pre-routed, the appointment date and its notes — only where the " +
          "line has none.",
        `- An alert: a waiting pre-route whose pickup is ${ALERT_BUSINESS_DAYS} business days away or less ` +
          "(weekends, US holidays and Yom Tov skipped), or already past (late).",
        "- New pre-routes are A2000 orders only: every open Deposco SO is a Worklist line from the moment it " +
          "exists. A Deposco pre-route added before (its SO #, SO230792) still moves, once its line is on the " +
          "Worklist (an SO with a DC waits for its group).",
        "- Cancelled / Shipped: A2000 closed the order with no pick ticket, or Deposco canceled or shipped the SO " +
          "before the pre-route moved.",
        "## Rules",
        "- Take every number from the tool answer. A count is within the page's view unless the question asks past it.",
        "- You only read: you cannot add, change, move or delete a pre-route, and must not offer to. The team adds " +
          "them on this page; the move is automatic.",
        p.manager ? "- The order value is in the answer for this manager." : "- Never give an order $ value here.",
        "- Answer briefly, with order #s, customers and pickup dates.",
      ].join("\n"),
    },
  ];
}

type Input = {
  tab?: string;
  source?: string;
  customer?: string[];
  search?: string;
  ignore_view?: boolean;
  limit?: number;
};

export function usePreRouteAskAi(page: PreRouteAskPage): void {
  const ref = useRef(page);
  ref.current = page;
  useEffect(() => {
    const app: AxaiApp = {
      systemPrompt: () => systemPrompt(ref.current),
      handleToolCall: async (name, input, appendStatus) => {
        if (name !== "query_pre_routes") return JSON.stringify({ error: `Unknown tool ${name}` });
        const p = ref.current;
        const { ignore_view, ...q } = (input ?? {}) as Input;
        appendStatus("Reading the pre-routes…");
        try {
          return JSON.stringify(
            await apiPost(askPath("pre-routes"), {
              ...q,
              ...(ignore_view
                ? // Past the view, still within my customers while it is on.
                  p.mine
                  ? { within: p.views.map((v) => v.record.id) }
                  : {}
                : {
                    within: p.viewIds,
                    tab: q.tab ?? p.tab,
                    ...(p.source !== "all" && !q.source ? { source: p.source } : {}),
                  }),
            }),
          );
        } catch (e) {
          return JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
        }
      },
    };
    window.__AXAI_APP__ = app;
    window.AXAI_TOOLS = PRE_ROUTE_TOOLS;
    document.body.classList.add("axai-ready");
    return () => {
      if (window.__AXAI_APP__ === app) delete window.__AXAI_APP__;
      window.AXAI_TOOLS = [];
      document.body.classList.remove("axai-ready");
    };
  }, []);
}
